import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  PlusIcon, 
  TrashIcon, 
  ServerIcon, 
  ArrowsRightLeftIcon, 
  ScaleIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import { HAProxyBackend, HAProxyServer } from '../../types';

interface BackendFormProps {
  backend: HAProxyBackend | null;
  onSave: (backend: Omit<HAProxyBackend, 'id' | 'instanceId'> & { id?: string, servers: HAProxyServer[] }) => void;
  onCancel: () => void;
  isOpen: boolean;
}

const BackendForm: React.FC<BackendFormProps> = ({ backend, onSave, onCancel, isOpen }) => {
  const [formData, setFormData] = useState<{
    id?: string;
    name: string;
    balance: string;
    servers: HAProxyServer[];
  }>({
    name: '',
    balance: 'roundrobin',
    servers: [],
  });

  // Load backend data when editing
  useEffect(() => {
    if (backend) {
      setFormData({
        id: backend.id,
        name: backend.name,
        balance: backend.balance,
        servers: backend.servers || [],
      });
    } else {
      // Reset form when adding new backend
      setFormData({
        name: '',
        balance: 'roundrobin',
        servers: [],
      });
    }
  }, [backend, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddServer = () => {
    const newServer: HAProxyServer = {
      id: `temp-${Date.now()}`,
      name: `server-${formData.servers.length + 1}`,
      address: '',
      httpPort: 80,
      httpsPort: 443,
      weight: 100,
      backendId: formData.id || '',
      healthCheck: true,
      proxyProtocolV2: false,
    };
    setFormData(prev => ({
      ...prev,
      servers: [...prev.servers, newServer]
    }));
  };

  const handleRemoveServer = (id: string) => {
    setFormData(prev => ({
      ...prev,
      servers: prev.servers.filter(server => server.id !== id)
    }));
  };

  const handleServerChange = (id: string, field: keyof HAProxyServer, value: any) => {
    setFormData(prev => ({
      ...prev,
      servers: prev.servers.map(server => 
        server.id === id 
          ? { ...server, [field]: value } 
          : server
      )
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Backend name is required.');
      return;
    }
    if (formData.servers.length === 0) {
      alert('At least one server is required.');
      return;
    }
    for (const server of formData.servers) {
      if (!server.name.trim() || !server.address.trim()) {
        alert('Server name and address are required for all servers.');
        return;
      }
    }
    onSave({
      ...formData,
      servers: formData.servers,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity"
        onClick={onCancel}
      ></div>
      
      {/* Modal content */}
      <div className="bg-zinc-900 rounded-xl shadow-2xl overflow-hidden w-full max-w-4xl relative z-10 max-h-[90vh] flex flex-col">
        {/* Header with close button */}
        <div className="border-b border-zinc-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AdjustmentsHorizontalIcon className="h-5 w-5 text-indigo-500" aria-hidden="true" />
              <h2 className="text-lg font-medium text-zinc-100">
                {backend ? 'Edit Backend' : 'Create New Backend'}
              </h2>
            </div>
            <button 
              type="button"
              onClick={onCancel}
              className="rounded-md bg-transparent text-zinc-400 hover:text-zinc-200 focus:outline-none"
              aria-label="Close modal"
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-grow">
          <div className="p-6 space-y-6">
            {/* Backend settings */}
            <div className="bg-zinc-800/50 rounded-lg overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center mb-4">
                  <ScaleIcon className="h-5 w-5 text-indigo-400 mr-2" />
                  <h3 className="text-sm font-medium text-zinc-300">Backend Configuration</h3>
                </div>
                
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <div className="sm:col-span-4">
                    <label htmlFor="name" className="block text-sm font-medium text-zinc-400">
                      Backend Name
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <ServerIcon className="h-4 w-4 text-zinc-500" aria-hidden="true" />
                      </div>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="e.g. web-backend"
                        className="block w-full rounded-md bg-zinc-700/50 border border-zinc-600 pl-10 py-2 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        required
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="balance" className="block text-sm font-medium text-zinc-400">
                      Balance Method
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <ArrowsRightLeftIcon className="h-4 w-4 text-zinc-500" aria-hidden="true" />
                      </div>
                      <select
                        id="balance"
                        name="balance"
                        value={formData.balance}
                        onChange={handleChange}
                        className="block w-full rounded-md bg-zinc-700/50 border border-zinc-600 pl-10 py-2 text-sm text-zinc-200 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                      >
                        <option value="roundrobin">Round Robin</option>
                        <option value="leastconn">Least Connections</option>
                        <option value="source">Source IP</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Servers section */}
            <div className="bg-zinc-800/50 rounded-lg overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <ServerIcon className="h-5 w-5 text-indigo-400 mr-2" />
                    <h3 className="text-sm font-medium text-zinc-300">Server Instances</h3>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddServer}
                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                  >
                    <PlusIcon className="h-4 w-4 mr-1.5" />
                    Add Server
                  </button>
                </div>

                {formData.servers.length === 0 ? (
                  <div className="bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700 p-6 text-center">
                    <ServerIcon className="mx-auto h-10 w-10 text-zinc-500" />
                    <h3 className="mt-2 text-sm font-medium text-zinc-400">No servers configured</h3>
                    <p className="mt-1 text-sm text-zinc-500">Get started by adding a server to this backend.</p>
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={handleAddServer}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                      >
                        <PlusIcon className="h-4 w-4 mr-1.5" />
                        Add Server
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Server list */}
                    {formData.servers.map((server) => (
                      <div key={server.id} className="relative group mb-3">
                        <div className="border border-zinc-700/50 rounded-lg overflow-hidden px-4 py-3 w-full transition-all duration-150 group-hover:border-indigo-500/50 group-hover:shadow-[0_0_0_1px_rgba(99,102,241,0.5)] flex flex-col gap-2">
                          {/* Row 1: Name, Address, HTTP Port, HTTPS Port */}
                          <div className="flex items-center gap-4 w-full">
                            <div className="w-32">
                              <label htmlFor={`server-name-${server.id}`} className="block text-xs font-medium text-zinc-400">Server Name</label>
                              <input
                                type="text"
                                id={`server-name-${server.id}`}
                                value={server.name}
                                onChange={(e) => handleServerChange(server.id, 'name', e.target.value)}
                                placeholder="server-name"
                                className="block w-full rounded-md bg-zinc-800/50 border border-zinc-700/50 py-1.5 px-3 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-500"
                                required
                              />
                            </div>
                            <div className="w-40">
                              <label htmlFor={`server-address-${server.id}`} className="block text-xs font-medium text-zinc-400">Server Address</label>
                              <input
                                type="text"
                                id={`server-address-${server.id}`}
                                value={server.address}
                                onChange={(e) => handleServerChange(server.id, 'address', e.target.value)}
                                placeholder="192.168.1.10"
                                className="block w-full rounded-md bg-zinc-800/50 border border-zinc-700/50 py-1.5 px-3 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-500"
                                required
                              />
                            </div>
                            <div className="w-24">
                              <label htmlFor={`server-http-port-${server.id}`} className="block text-xs font-medium text-zinc-400">HTTP Port</label>
                              <input
                                type="number"
                                id={`server-http-port-${server.id}`}
                                value={server.httpPort}
                                onChange={(e) => handleServerChange(server.id, 'httpPort', parseInt(e.target.value, 10))}
                                placeholder="80"
                                className="block w-full rounded-md bg-zinc-800/50 border border-zinc-700/50 py-1.5 px-3 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-500"
                                min="1"
                                max="65535"
                                required
                              />
                            </div>
                            <div className="w-24">
                              <label htmlFor={`server-https-port-${server.id}`} className="block text-xs font-medium text-zinc-400">HTTPS Port</label>
                              <input
                                type="number"
                                id={`server-https-port-${server.id}`}
                                value={server.httpsPort}
                                onChange={(e) => handleServerChange(server.id, 'httpsPort', parseInt(e.target.value, 10))}
                                placeholder="443"
                                className="block w-full rounded-md bg-zinc-800/50 border border-zinc-700/50 py-1.5 px-3 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-500"
                                min="1"
                                max="65535"
                                required
                              />
                            </div>
                          </div>
                          {/* Row 2: Weight, Health, Proxy Protocol, Delete */}
                          <div className="flex items-center gap-4 w-full mt-2">
                            <div className="w-20">
                              <label htmlFor={`server-weight-${server.id}`} className="block text-xs font-medium text-zinc-400">Weight</label>
                              <input
                                type="number"
                                id={`server-weight-${server.id}`}
                                value={server.weight}
                                onChange={(e) => handleServerChange(server.id, 'weight', parseInt(e.target.value, 10))}
                                placeholder="100"
                                className="block w-full rounded-md bg-zinc-800/50 border border-zinc-700/50 py-1.5 px-3 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-500"
                                min="0"
                                max="256"
                                required
                              />
                            </div>
                            <div className="w-32">
                              <label htmlFor={`server-healthCheck-${server.id}`} className="block text-xs font-medium text-zinc-400">Health Check</label>
                              <div className="flex items-center mt-1">
                                <label className="inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    id={`server-healthCheck-${server.id}`}
                                    checked={!!server.healthCheck}
                                    onChange={(e) => handleServerChange(server.id, 'healthCheck', e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="relative w-11 h-6 bg-zinc-800/50 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                              </div>
                            </div>
                            <div className="w-48">
                              <label htmlFor={`server-proxyv2-${server.id}`} className="block text-xs font-medium text-zinc-400">Send PROXY protocol v2</label>
                              <div className="flex items-center mt-1">
                                <label className="inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    id={`server-proxyv2-${server.id}`}
                                    checked={!!server.proxyProtocolV2}
                                    onChange={(e) => handleServerChange(server.id, 'proxyProtocolV2', e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="relative w-11 h-6 bg-zinc-800/50 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                              </div>
                            </div>
                            <div className="ml-auto">
                              <button
                                type="button"
                                onClick={() => handleRemoveServer(server.id)}
                                className="text-zinc-400 hover:text-red-400 focus:outline-none transition-colors duration-150 group-hover:scale-110 mr-2"
                                title="Remove Server"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-800 px-6 py-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
            >
              {backend ? 'Update Backend' : 'Create Backend'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BackendForm; 